"use client";
"use no memo";

// TanStack Virtual exposes mutable instances that React Compiler cannot safely memoize.
// Remove this boundary when the library publishes compiler-compatible APIs.
import { useVirtualizer } from "@tanstack/react-virtual";
import { ArrowLeft, ArrowRight, Download, Minus, Plus, RotateCcw } from "lucide-react";
import { TextLayer, PDFDocumentProxy } from "pdfjs-dist";
import { useEffect, useRef, useState, useMemo, createRef, useCallback, useDeferredValue, type Ref, type RefObject } from "react";
import { FileDownloadButton } from "@/src/components/file-viewer/file-download-button";
import { drawMetadata, clearHighlightCanvases } from "@/src/components/pdf-viewer/canvas-utils";
import { calculateMostVisiblePage } from "@/src/components/pdf-viewer/document-utils";
import { usePdfViewerInteractionContext } from "@/src/components/pdf-viewer/interaction-provider";
import { CancelableTask, PDFDocumentData } from "@/src/components/pdf-viewer/types";
import { usePdf } from "@/src/components/pdf-viewer/use-pdf";
import { Button } from "@/src/components/shadcn/ui/button";
import { Skeleton } from "@/src/components/shadcn/ui/skeleton";
import { logger } from "@/src/lib/logging/logger";
import { rotateNormalizedBoundingBox } from "@/src/lib/pdf-page-geometry";
import { cn } from "@/src/lib/shadcn-utils";
import { throttle } from "@/src/lib/throttle";
import { ExtractionMetadata } from "@/src/models/section";
interface PageSize {
  height: number;
  width: number;
}
export function PDFLoadingSkeleton() {
  return <div className="flex h-full w-full min-h-0 min-w-0 flex-col">
      <div className="flex h-11 min-w-0 flex-shrink-0 flex-wrap items-center justify-between gap-x-2 gap-y-1 border-b p-2">
        <div className="flex min-w-0 shrink-0 items-center justify-start">
          <Button size="icon-sm" variant="ghost" disabled className="text-muted-foreground">
            <ArrowLeft size={16} />
          </Button>
          <div className="min-w-[44px] px-1">
            <Skeleton className="h-3 w-full" />
          </div>
          <Button size="icon-sm" variant="ghost" disabled className="text-muted-foreground">
            <ArrowRight size={16} />
          </Button>
        </div>
        <div className="flex shrink-0 items-center">
          <Button size="icon-sm" variant="ghost" disabled className="text-muted-foreground">
            <Minus size={16} />
          </Button>
          <span className="min-w-[50px] text-center text-muted-foreground text-xs">130%</span>
          <Button size="icon-sm" variant="ghost" disabled className="text-muted-foreground">
            <Plus size={16} />
          </Button>
        </div>
        <div className="flex shrink-0 items-center justify-end">
          <Button size="icon-sm" variant="ghost" disabled className="text-muted-foreground">
            <RotateCcw size={16} />
          </Button>
          <Button size="icon-sm" variant="ghost" disabled className="text-muted-foreground">
            <Download size={16} />
          </Button>
        </div>
      </div>
      <div className="relative flex flex-1 flex-col items-center justify-start overflow-auto bg-muted/40 pt-4 pb-6">
        <Skeleton className="mx-auto h-full w-[80%] rounded-sm" />
      </div>
    </div>;
}
function PDFError({
  error
}: {
  error: Error;
}) {
  return <div className="h-full flex items-center justify-center overflow-scrolls">
      <p className="m-2 text-sm break-words overflow-hidden">Error loading file: {error?.message ?? "Unknown error"}</p>
    </div>;
}
function usePdfData({
  document
}: {
  document: PDFDocumentProxy | undefined;
}): PDFDocumentData[] | undefined {
  const documentDataRef = useRef<PDFDocumentData[]>([]);
  return useMemo(() => {
    if (!document) {
      documentDataRef.current = [];
      return undefined;
    }
    const data = Array.from({
      length: document.numPages
    }, (_, pageNumber) => {
      const existing = documentDataRef.current[pageNumber];
      if (existing) {
        return {
          ...existing,
          pageNumber,
          viewport: null
        };
      }
      return {
        pageNumber,
        canvasRef: createRef<HTMLCanvasElement | null>(),
        highlightRef: createRef<HTMLCanvasElement | null>(),
        textLayerRef: createRef<HTMLDivElement | null>(),
        viewport: null
      };
    });
    documentDataRef.current = data;
    return data;
  }, [document]);
}
function assignRef<T>(ref: Ref<T | null> | undefined, value: T | null) {
  if (!ref) {
    return;
  }
  if (typeof ref === "function") {
    ref(value);
  } else {
    (ref as RefObject<T | null>).current = value;
  }
}
export function PDFViewer({
  url,
  activePage: propActivePage,
  filename,
  downloadDisabledReason,
  disableTextSelection = false,
  scrollContainerRef,
  pageFrameClassNames
}: {
  url: string;
  activePage?: number;
  filename?: string;
  downloadDisabledReason?: string;
  disableTextSelection?: boolean;
  scrollContainerRef?: Ref<HTMLDivElement | null>;
  pageFrameClassNames?: ReadonlyArray<string | null | undefined>;
}) {
  const {
    metadata,
    activePage: contextActivePage,
    boundingBoxStyle,
    focusedBoundingBoxIndex,
    hideNonFocusedBoxes
  } = usePdfViewerInteractionContext();
  const activePage = propActivePage !== undefined ? propActivePage : contextActivePage;
  const {
    data: pdfDocument,
    isLoading,
    error
  } = usePdf(url);
  const documentData = usePdfData({
    document: pdfDocument
  });
  const [pageRendering, setPageRendering] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const deferredCurrentPage = useDeferredValue(currentPage);
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [hasDownloaded, setHasDownloaded] = useState(false);
  const [basePageSize, setBasePageSize] = useState<PageSize>({
    height: 792,
    width: 612
  });
  const containerRef = useRef<HTMLDivElement | null>(null);
  const setScrollContainerNode = useCallback((node: HTMLDivElement | null) => {
    containerRef.current = node;
    assignRef(scrollContainerRef, node);
  }, [scrollContainerRef]);
  const asyncTasksRef = useRef<Set<CancelableTask>>(new Set());
  const lastMetadataFocusKeyRef = useRef<string | null>(null);
  const focusedBoundingBox = metadata?.bboxes?.[typeof focusedBoundingBoxIndex === "number" ? focusedBoundingBoxIndex : 0];
  const metadataTargetPage = focusedBoundingBox?.page;
  const targetPage = metadataTargetPage ?? activePage;
  const renderKey = `${scale}:${rotation}`;
  const metadataFocusKey = focusedBoundingBox ? `${renderKey}:${focusedBoundingBoxIndex ?? 0}:${focusedBoundingBox.page}:${focusedBoundingBox.bbox.xywh.join(",")}` : null;
  const currentRenderKeyRef = useRef(renderKey);
  currentRenderKeyRef.current = renderKey;
  const estimatedPageSize = rotation % 180 === 0 ? {
    height: basePageSize.height * scale,
    width: basePageSize.width * scale
  } : {
    height: basePageSize.width * scale,
    width: basePageSize.height * scale
  };
  const pageVirtualizer = useVirtualizer({
    count: documentData?.length ?? 0,
    estimateSize: () => estimatedPageSize.height,
    gap: 16,
    getScrollElement: () => containerRef.current,
    overscan: 3
  });
  const virtualPages = pageVirtualizer.getVirtualItems();
  const virtualPageIndexes = useMemo(() => virtualPages.map(page => page.index), [virtualPages]);

  // Jump to externally selected pages. Metadata targets take precedence over activePage.
  useEffect(() => {
    if (typeof targetPage === "number" && targetPage >= 0 && targetPage < (documentData?.length ?? 0) && hasLoaded) {
      setCurrentPage(targetPage);
      pageVirtualizer.scrollToIndex(targetPage, {
        align: "start"
      });
    }
  }, [documentData, hasLoaded, pageVirtualizer, targetPage]);

  // Track which rendered page occupies the most viewport space.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !documentData) {
      return;
    }
    const updateCurrentPage = throttle(() => {
      const page_0 = calculateMostVisiblePage(container, documentData);
      if (page_0) setCurrentPage(page_0.pageNumber);
    }, 50);
    container.addEventListener("scroll", updateCurrentPage, {
      passive: true
    });
    return () => {
      container.removeEventListener("scroll", updateCurrentPage);
    };
  }, [documentData]);
  useEffect(() => {
    lastMetadataFocusKeyRef.current = null;
  }, [documentData]);

  // Highlight metadata
  useEffect(() => {
    requestAnimationFrame(() => {
      if (!documentData) {
        return;
      }
      if (!metadata) {
        lastMetadataFocusKeyRef.current = null;
        if (!pageRendering) clearHighlightCanvases(documentData);
        return;
      }
      if (pageRendering) {
        return;
      }
      drawMetadata({
        documentData,
        metadata,
        style: boundingBoxStyle,
        focusedIndex: focusedBoundingBoxIndex,
        hideNonFocusedBoxes
      });
      if (metadataFocusKey && lastMetadataFocusKeyRef.current !== metadataFocusKey) {
        focusMetadata({
          documentData,
          metadata,
          focusedIndex: focusedBoundingBoxIndex
        });
      }
    });
  }, [metadata, documentData, boundingBoxStyle, focusedBoundingBoxIndex, hideNonFocusedBoxes, metadataFocusKey, pageRendering]);
  function focusMetadata({
    documentData: documentData_0,
    metadata: metadata_0,
    focusedIndex
  }: {
    documentData: PDFDocumentData[];
    metadata: ExtractionMetadata;
    focusedIndex?: number | null;
  }) {
    const index = typeof focusedIndex === "number" ? focusedIndex : 0;
    const scrollBehavior: ScrollBehavior = "instant";
    const boundingBox = metadata_0.bboxes?.[index];
    if (!boundingBox) {
      return;
    }
    const [x, y, width, height] = boundingBox.bbox.xywh;
    const pageData = documentData_0[boundingBox.page];
    if (!pageData) {
      return;
    }
    const canvas = pageData.canvasRef.current;
    const {
      viewport
    } = pageData;
    if (!containerRef.current || !canvas || !viewport || canvas.dataset.renderKey !== renderKey) {
      return;
    }
    const [viewportX, viewportY] = rotateNormalizedBoundingBox([x, y, width, height], viewport.rotation);
    const scaledX = viewportX * viewport.width;
    const scaledY = viewportY * viewport.height;
    const containerRect = containerRef.current.getBoundingClientRect();
    const pageTop = containerRef.current.scrollTop + canvas.getBoundingClientRect().top - containerRect.top;
    const buffer = 20;
    containerRef.current.scrollTo({
      top: pageTop + scaledY - buffer,
      left: scaledX - buffer,
      behavior: scrollBehavior
    });
    lastMetadataFocusKeyRef.current = metadataFocusKey;
  }
  const autoScale = useCallback(async () => {
    if (!pdfDocument || !containerRef.current) {
      return;
    }
    const containerWidth = Math.min(containerRef.current.getBoundingClientRect().width, 1000);
    // Check the first two pages to get the max width of the doc.
    const pages = (await Promise.all([pdfDocument.getPage(1), pdfDocument.numPages > 1 ? pdfDocument.getPage(2) : undefined])).filter(page_1 => page_1 !== undefined);
    const viewports = pages.map(page_2 => page_2.getViewport({
      scale: 1
    }));
    const firstViewport = viewports[0];
    setBasePageSize({
      height: firstViewport.height,
      width: firstViewport.width
    });
    const bufferX = 20 * 2;
    const newScale = containerWidth / (Math.max(...viewports.map(viewport_0 => viewport_0.width)) + bufferX);
    setScale(parseFloat(newScale.toFixed(1)));
  }, [pdfDocument]);
  const autoScaleOnLoad = useCallback(async () => {
    await autoScale();
    setHasLoaded(true);
  }, [setHasLoaded, autoScale]);
  const cancelRunningTasks = useCallback(() => {
    for (const cancelTask of asyncTasksRef.current) {
      try {
        cancelTask();
      } catch (error_0) {
        logger.debug("Failed to cancel PDF render task", {
          error: error_0
        });
      }
    }
    asyncTasksRef.current.clear();
  }, []);
  const renderText = useCallback(async (data: PDFDocumentData, expectedRenderKey: string): Promise<boolean> => {
    const textLayerRef = data.textLayerRef.current;
    if (!pdfDocument || !textLayerRef || !data.viewport) {
      return false;
    }
    const page_3 = await pdfDocument.getPage(data.pageNumber + 1);
    const textContent = await page_3.getTextContent();
    if (data.textLayerRef.current !== textLayerRef || currentRenderKeyRef.current !== expectedRenderKey) {
      return false;
    }

    // Clear any previous text
    textLayerRef.innerHTML = "";

    // A page without text still has a complete text layer.
    if (textContent.items.length < 1) {
      return true;
    }
    const textLayer = new TextLayer({
      container: textLayerRef,
      textContentSource: textContent,
      viewport: data.viewport
    });
    const cancelTextLayer = () => {
      textLayer.cancel();
    };
    asyncTasksRef.current.add(cancelTextLayer);
    try {
      await textLayer.render();
      return data.textLayerRef.current === textLayerRef && currentRenderKeyRef.current === expectedRenderKey;
    } catch {
      // Canceled or failed text layers must be retried.
      return false;
    } finally {
      asyncTasksRef.current.delete(cancelTextLayer);
    }
  }, [pdfDocument]);
  const renderPage = useCallback(async (data_0: PDFDocumentData, expectedRenderKey_0: string) => {
    if (!pdfDocument) return;
    try {
      const page_4 = await pdfDocument.getPage(data_0.pageNumber + 1);
      const viewport_1 = page_4.getViewport({
        scale,
        rotation: ((page_4.rotate ?? 0) + rotation) % 360
      });
      data_0.viewport = viewport_1;
      const pdfCanvas = data_0.canvasRef.current;
      const highlightCanvas = data_0.highlightRef.current;
      const textLayerRef_0 = data_0.textLayerRef.current;
      if (!pdfCanvas || !highlightCanvas || !textLayerRef_0) {
        return;
      }
      const pixelRatio = window.devicePixelRatio ?? 1;
      const canvasHeight = Math.floor(viewport_1.height * pixelRatio);
      const canvasWidth = Math.floor(viewport_1.width * pixelRatio);
      const offscreenCanvas = document.createElement("canvas");
      offscreenCanvas.height = canvasHeight;
      offscreenCanvas.width = canvasWidth;
      const offscreenCtx = offscreenCanvas.getContext("2d");
      if (!offscreenCtx) {
        return;
      }
      const renderContext = {
        canvasContext: offscreenCtx,
        viewport: viewport_1
      };
      offscreenCtx.scale(pixelRatio, pixelRatio);
      const renderTask = page_4.render(renderContext);
      const cancelRender = () => {
        renderTask.cancel();
      };
      asyncTasksRef.current.add(cancelRender);
      try {
        await renderTask.promise;
        // eslint-disable-next-line unused-imports/no-unused-vars
      } catch (err_0) {
        // do nothing if task is canceled.
        return;
      } finally {
        asyncTasksRef.current.delete(cancelRender);
      }

      // Skip stale output when refs were replaced while rendering.
      if (data_0.canvasRef.current !== pdfCanvas || data_0.highlightRef.current !== highlightCanvas || data_0.textLayerRef.current !== textLayerRef_0 || currentRenderKeyRef.current !== expectedRenderKey_0) {
        return;
      }
      pdfCanvas.height = canvasHeight;
      pdfCanvas.width = canvasWidth;
      pdfCanvas.style.height = `${viewport_1.height}px`;
      pdfCanvas.style.width = `${viewport_1.width}px`;
      highlightCanvas.height = canvasHeight;
      highlightCanvas.width = canvasWidth;
      highlightCanvas.style.height = `${viewport_1.height}px`;
      highlightCanvas.style.width = `${viewport_1.width}px`;
      textLayerRef_0.style.height = `${viewport_1.height}px`;
      textLayerRef_0.style.width = `${viewport_1.width}px`;
      const pdfCtx = pdfCanvas.getContext("2d");
      const highlightCtx = highlightCanvas.getContext("2d");
      if (!pdfCtx || !highlightCtx) {
        return;
      }
      pdfCtx.clearRect(0, 0, canvasWidth, canvasHeight);
      pdfCtx.drawImage(offscreenCanvas, 0, 0);
      highlightCtx.scale(pixelRatio, pixelRatio);
      const textLayerComplete = disableTextSelection || (await renderText(data_0, expectedRenderKey_0));
      if (!textLayerComplete || data_0.canvasRef.current !== pdfCanvas || data_0.highlightRef.current !== highlightCanvas || data_0.textLayerRef.current !== textLayerRef_0 || currentRenderKeyRef.current !== expectedRenderKey_0) {
        return;
      }

      // The scheduler skips only fully rendered pages.
      pdfCanvas.dataset.renderKey = expectedRenderKey_0;
    } catch (err) {
      logger.error("Error rendering page:", {
        err
      });
    }
  }, [pdfDocument, renderText, rotation, scale, disableTextSelection]);

  // Render only the mounted viewport window, in bounded batches.
  useEffect(() => {
    if (!documentData?.length) return;
    if (!hasLoaded) {
      void autoScaleOnLoad();
      return;
    }
    let cancelled = false;
    const frame = requestAnimationFrame(() => {
      const render = async () => {
        setPageRendering(true);
        const pagesToRender = virtualPageIndexes.filter(pageNumber => {
          const canvas_0 = documentData[pageNumber]?.canvasRef.current;
          return canvas_0 && canvas_0.dataset.renderKey !== renderKey;
        });
        await Promise.all(pagesToRender.map(pageNumber_0 => renderPage(documentData[pageNumber_0], renderKey)));
        if (!cancelled) setPageRendering(false);
      };
      void render();
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      cancelRunningTasks();
    };
  }, [autoScaleOnLoad, cancelRunningTasks, documentData, hasLoaded, renderKey, renderPage, virtualPageIndexes]);
  function rotatePDF() {
    setRotation(rotation_0 => (rotation_0 + 270) % 360);
  }
  function changePage(offset: number) {
    if (!documentData) {
      return;
    }
    const newPage = deferredCurrentPage + offset;
    if (newPage < 0 || newPage > documentData.length - 1) {
      return;
    }
    setCurrentPage(newPage);
    pageVirtualizer.scrollToIndex(newPage, {
      align: "start"
    });
  }
  if (isLoading) {
    return <PDFLoadingSkeleton />;
  }
  if (!pdfDocument) {
    return <PDFError error={error as Error} />;
  }
  return <div className="flex min-h-0 min-w-0 flex-col w-full h-full">
      <div className="flex min-w-0 flex-shrink-0 flex-wrap items-center justify-between gap-x-2 gap-y-1 border-b p-2">
        <div className="flex min-w-0 shrink-0 items-center justify-start">
          <Button size="icon-sm" variant="ghost" onClick={() => changePage(-1)} disabled={deferredCurrentPage <= 0} className="text-muted-foreground">
            <ArrowLeft size={16} />
          </Button>
          <span className="min-w-[44px] text-center text-muted-foreground text-xs">
            {deferredCurrentPage + 1} / {pdfDocument.numPages}
          </span>
          <Button size="icon-sm" variant="ghost" onClick={() => changePage(1)} disabled={deferredCurrentPage >= pdfDocument.numPages - 1} className="text-muted-foreground">
            <ArrowRight size={16} />
          </Button>
        </div>
        <div className="flex shrink-0 items-center">
          <Button size="icon-sm" variant="ghost" onClick={() => setScale(scale_0 => Math.max(0.1, scale_0 - 0.1))} className="text-muted-foreground">
            <Minus size={16} />
          </Button>
          <span className="min-w-[50px] text-center text-muted-foreground text-xs">{Math.round(scale * 100)}%</span>
          <Button size="icon-sm" variant="ghost" onClick={() => setScale(scale_1 => Math.min(3, scale_1 + 0.1))} className="text-muted-foreground">
            <Plus size={16} />
          </Button>
        </div>
        <div className="flex shrink-0 items-center justify-end">
          <Button size="icon-sm" variant="ghost" className="text-muted-foreground" onClick={rotatePDF}>
            <RotateCcw size={16} />
          </Button>
          <FileDownloadButton disabledReason={downloadDisabledReason} onClick={async () => {
          try {
            const pdfData = await pdfDocument.getData();

            // Check if File System Access API is supported using feature detection
            // @ts-ignore - File System Access API may not be recognized by TypeScript
            if (typeof window.showSaveFilePicker === "function") {
              try {
                // Use the File System Access API to save the file
                // This allows users to select a download location
                // @ts-ignore - Using the File System Access API
                const fileHandle = await window.showSaveFilePicker({
                  suggestedName: filename || "document.pdf",
                  types: [{
                    description: "PDF Files",
                    accept: {
                      "application/pdf": [".pdf"]
                    }
                  }]
                });

                // @ts-ignore - Using the File System Access API
                const writable = await fileHandle.createWritable();
                await writable.write(pdfData);
                await writable.close();
                setHasDownloaded(true);
                return;
              } catch (saveError: unknown) {
                // User cancelled the save dialog - don't proceed to fallback
                logger.info("Save picker was cancelled:", {
                  error: saveError
                });

                // Only proceed to fallback if error doesn't seem like a user cancellation
                // The AbortError is what's thrown when a user cancels the dialog
                if (saveError instanceof Error && saveError.name !== "AbortError") {
                  throw saveError; // Re-throw to trigger the fallback
                }
                return; // Exit early if user cancelled
              }
            }

            // Fall back to regular download if API not supported
            const a = document.createElement("a");
            a.href = url;
            a.download = filename || "document.pdf";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setHasDownloaded(true);
          } catch (error_1) {
            logger.error("Error downloading file:", {
              error: error_1
            });
          }
        }} hasDownloaded={hasDownloaded} />
        </div>
      </div>
      <div className="relative flex-1 overflow-auto bg-muted/40 p-2" ref={setScrollContainerNode}>
        <div className="relative min-w-full" style={{
        height: pageVirtualizer.getTotalSize()
      }}>
          {virtualPages.map(virtualPage => {
          const data_1 = documentData?.[virtualPage.index];
          if (!data_1) return null;
          const hasCurrentViewport = data_1.canvasRef.current?.dataset.renderKey === renderKey && data_1.viewport !== null;
          const pageSize = hasCurrentViewport && data_1.viewport ? data_1.viewport : estimatedPageSize;
          const userUnit = hasCurrentViewport ? data_1.viewport?.userUnit ?? 1 : 1;
          return <div key={virtualPage.key} ref={pageVirtualizer.measureElement} data-index={virtualPage.index} className="absolute top-0 left-0 flex min-w-full justify-center" style={{
            height: pageSize.height,
            transform: `translateY(${virtualPage.start}px)`,
            width: pageSize.width
          }}>
                <div className={cn("relative isolate shrink-0 bg-white", pageFrameClassNames?.[data_1.pageNumber])} style={{
              height: pageSize.height,
              width: pageSize.width
            }}>
                  <canvas ref={data_1.canvasRef} className="absolute inset-0 h-full w-full border border-gray-300 shadow-md" />
                  <canvas ref={data_1.highlightRef} className="absolute inset-0 h-full w-full pointer-events-none z-10" />
                  <div ref={data_1.textLayerRef} className="absolute inset-0 z-20 opacity-20 text-transparent leading-[1] overflow-hidden [&>span]:absolute [&>span]:-ml-[1ch]" data-main-rotation={rotation} style={{
                // see https://github.com/mozilla/pdf.js/blob/d009e4b3a743661d91d4c0d97367b7abe51d2b18/web/pdf_viewer.css#L110
                // @ts-expect-error css var ok
                "--total-scale-factor": scale * userUnit,
                "--user-unit": userUnit,
                "--scale-round-y": "1px",
                "--scale-round-x": "1px"
              }} />
                </div>
                {data_1.pageNumber < pdfDocument.numPages - 1 ? <hr className="absolute -bottom-2 left-0 h-px w-full border border-dashed border-accent-foreground" /> : null}
              </div>;
        })}
        </div>
      </div>
    </div>;
}